import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Bell, Check, Trash2, Filter, AlertTriangle, Info, CheckCircle, Clock,
  ExternalLink, FileText, Users, MessageSquare, Package, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_ALERTA = [
  { value: 'Documentos', label: 'Documentos', icon: FileText, color: 'text-red-500' },
  { value: 'Validação', label: 'Validação', icon: CheckCircle, color: 'text-blue-500' },
  { value: 'Candidatura', label: 'Candidaturas', icon: Users, color: 'text-purple-500' },
  { value: 'Ticket', label: 'Tickets', icon: MessageSquare, color: 'text-green-500' },
  { value: 'Pedido', label: 'Pedidos', icon: Package, color: 'text-orange-500' },
  { value: 'Campanha', label: 'Campanhas', icon: Calendar, color: 'text-amber-500' },
];

const PRIORIDADES = [
  { value: 'Crítica', color: 'bg-red-500' },
  { value: 'Alta', color: 'bg-orange-500' },
  { value: 'Média', color: 'bg-yellow-500' },
  { value: 'Baixa', color: 'bg-blue-500' },
];

export default function AlertasPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTipo, setFilterTipo] = useState('');

  useEffect(() => {
    loadAlertas();
  }, [filterStatus, filterTipo]);

  const loadAlertas = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterTipo) params.tipo = filterTipo;
      const data = await api.getAlertas(params);
      setAlertas(data);
    } catch (error) {
      console.error('Error loading alertas:', error);
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLida = async (alertaId, e) => {
    e.stopPropagation();
    try {
      await api.marcarAlertaLida(alertaId);
      toast.success('Alerta marcado como lido');
      loadAlertas();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Erro ao marcar alerta');
    }
  };

  const handleDelete = async (alertaId, e) => {
    e.stopPropagation();
    try {
      await api.deleteAlerta(alertaId);
      toast.success('Alerta eliminado');
      loadAlertas();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Erro ao eliminar alerta');
    }
  };

  const handleNavigate = (alerta) => {
    if (alerta.link_to) {
      navigate(alerta.link_to);
    }
  };

  const getAlertaIcon = (tipo) => {
    const tipoConfig = TIPOS_ALERTA.find(t => t.value === tipo);
    if (!tipoConfig) return Bell;
    return tipoConfig.icon;
  };

  const getAlertaColor = (tipo) => {
    const tipoConfig = TIPOS_ALERTA.find(t => t.value === tipo);
    if (!tipoConfig) return 'text-slate-500';
    return tipoConfig.color;
  };

  const getPrioridadeColor = (prioridade) => {
    const prioConfig = PRIORIDADES.find(p => p.value === prioridade);
    return prioConfig?.color || 'bg-slate-500';
  };

  const pendentesCount = alertas.filter(a => a.status === 'Pendente').length;
  const lidasCount = alertas.filter(a => a.status === 'Lida').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="alertas-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alertas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Alertas e Notificações</h1>
          <p className="text-slate-500 mt-1">Centro de notificações da plataforma</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="py-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500 mr-2"></span>
            {pendentesCount} pendentes
          </Badge>
          <Badge variant="outline" className="py-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
            {lidasCount} lidas
          </Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {TIPOS_ALERTA.map((tipo) => {
          const Icon = tipo.icon;
          const count = alertas.filter(a => a.tipo === tipo.value).length;
          return (
            <Card 
              key={tipo.value} 
              className={`cursor-pointer transition-all hover:shadow-md ${
                filterTipo === tipo.value ? 'ring-2 ring-[#0066CC]' : ''
              }`}
              onClick={() => setFilterTipo(filterTipo === tipo.value ? '' : tipo.value)}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${tipo.color}`} />
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-xs text-slate-500">{tipo.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filtrar por:</span>
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterStatus === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('')}
            className={filterStatus === '' ? 'bg-[#0066CC]' : ''}
          >
            Todos
          </Button>
          <Button
            variant={filterStatus === 'Pendente' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('Pendente')}
            className={filterStatus === 'Pendente' ? 'bg-[#0066CC]' : ''}
          >
            Pendentes
          </Button>
          <Button
            variant={filterStatus === 'Lida' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('Lida')}
            className={filterStatus === 'Lida' ? 'bg-[#0066CC]' : ''}
          >
            Lidos
          </Button>
        </div>
        {filterTipo && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterTipo('')}
            className="text-red-600"
          >
            Limpar filtro tipo
          </Button>
        )}
      </div>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#0066CC]" />
            Notificações ({alertas.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertas.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Sem alertas para mostrar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alertas.map((alerta) => {
                const Icon = getAlertaIcon(alerta.tipo);
                return (
                  <div
                    key={alerta.id}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      alerta.status === 'Pendente' 
                        ? 'bg-white border-slate-200 hover:border-[#0066CC] hover:shadow-md' 
                        : 'bg-slate-50 border-slate-100 opacity-70 hover:opacity-100'
                    }`}
                    onClick={() => handleNavigate(alerta)}
                    data-testid={`alerta-${alerta.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        alerta.status === 'Pendente' ? 'bg-slate-100' : 'bg-slate-200'
                      }`}>
                        <Icon className={`w-5 h-5 ${getAlertaColor(alerta.tipo)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{alerta.titulo}</h4>
                              {alerta.link_to && (
                                <ExternalLink className="w-4 h-4 text-[#0066CC]" />
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{alerta.descricao}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={`${getPrioridadeColor(alerta.prioridade)} text-white text-xs`}>
                              {alerta.prioridade}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {alerta.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-slate-400">
                            {new Date(alerta.created_at).toLocaleString('pt-PT')}
                          </span>
                          <div className="flex items-center gap-2">
                            {alerta.status === 'Pendente' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleMarcarLida(alerta.id, e)}
                                className="text-[#0066CC] hover:text-[#0052a3]"
                                data-testid={`mark-read-${alerta.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Marcar lido
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDelete(alerta.id, e)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${alerta.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
